import { useServiceError } from '@/hooks';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import {
  createPaymentMethodInvoice,
  deletePaymentMethodInvoice,
  getPaymentMethodInvoiceId,
  listPaymentMethodInvoice,
  updatePaymentMethodInvoice,
} from './PaymentMethod.service';
import { PaymentMethodCreateRequestDTO } from './PaymentMethod.types';

export function usePaymentMethods(id: number) {
  const query = useInfiniteQuery(['paymentMethodInvoice', id], () =>
    listPaymentMethodInvoice(id),
  );

  useServiceError(query.error);

  return query;
}

export function usePaymentMethodInvoice() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: PaymentMethodCreateRequestDTO) => createPaymentMethodInvoice(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'invoice.invoicePaymentCondition.notifications.success.title',
          }),
          description: intl.formatMessage({
            id: 'invoice.invoicePaymentCondition.notifications.create.success.description',
          }),
        });

        queryClient.invalidateQueries(['paymentMethodInvoice']);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useDeletePaymentMethodInvoice() {
  const intl = useIntl();

  const queryClient = useQueryClient();

  const mutation = useMutation(
    ({ invoiceId, id }: { invoiceId: number; id: number }) =>
      deletePaymentMethodInvoice(invoiceId, id),
    {
      async onSuccess(_, vars) {
        notification.success({
          message: intl.formatMessage({
            id: 'invoice.invoicePaymentCondition.notifications.success.title',
          }),
          description: intl.formatMessage({
            id: 'invoice.invoicePaymentCondition.notifications.delete.success.description',
          }),
        });

        await queryClient.invalidateQueries(['paymentMethodInvoice']);
        await queryClient.invalidateQueries([
          'TEF-PAYMENT-AVAILABILITY',
          vars.invoiceId,
        ]);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useGetPaymentMethodInvoiceId() {
  const mutation = useMutation(
    ({ invoiceId, id }: { invoiceId: number; id: number }) =>
      getPaymentMethodInvoiceId(invoiceId, id),
  );
  useServiceError(mutation.error);
  return mutation;
}

export function useUpdatePaymentMethodInvoice() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: PaymentMethodCreateRequestDTO) => updatePaymentMethodInvoice(obj),
    {
      onSuccess() {
        notification.success({
          message: intl({
            id: 'invoice.invoicePaymentCondition.notifications.success.title',
          }),
          description: intl({
            id: 'invoice.invoicePaymentCondition.notifications.update.success.description',
          }),
        });

        queryClient.invalidateQueries(['paymentMethodInvoice']);
      },
    },
  );
  useServiceError(mutation.error);
  return mutation;
}
